import { type ReactNode, Suspense, lazy, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useAuthConfig, useIsOwner, useMe } from "./api/hooks";
import { AppShell } from "./components/AppShell";
import { Spinner } from "./components/Spinner";
import { useAuthStore, useIsAuthenticated } from "./stores/auth";

// Route-level splitting: the chart bundle, the graph editor and the analytics charts are
// each large and each used on one route, so nobody pays for a page they did not open.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const StockDetail = lazy(() => import("./pages/StockDetail"));
const Screen = lazy(() => import("./pages/Screen"));
const Strategies = lazy(() => import("./pages/Strategies"));
const StrategyEditor = lazy(() => import("./pages/StrategyEditor"));
const Reports = lazy(() => import("./pages/Reports"));
const BacktestReport = lazy(() => import("./pages/BacktestReport"));
const Assistant = lazy(() => import("./pages/Assistant"));
const Signals = lazy(() => import("./pages/Signals"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const Login = lazy(() => import("./pages/Login"));

export function App() {
  const { data: config, isLoading: configLoading } = useAuthConfig();
  const authenticated = useIsAuthenticated();
  const hydrated = useAuthStore((state) => state.hydrated);

  // A local single-user install has no login screen: a password prompt with no security
  // benefit is pure friction.
  const authRequired = Boolean(config && !config.auth_disabled);
  const { isLoading: meLoading, isError } = useMe(authRequired && authenticated);

  if (configLoading || !hydrated) {
    return <FullPageSpinner label="正在启动" />;
  }

  if (authRequired && !authenticated) {
    return (
      <Suspense fallback={<FullPageSpinner />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    );
  }

  if (authRequired && meLoading) {
    return <FullPageSpinner label="正在验证身份" />;
  }

  if (authRequired && isError) {
    // The stored refresh token was rejected. Clearing it sends the user to login rather
    // than leaving them in a loop of failing requests.
    return <SessionExpired />;
  }

  return (
    <AppShell>
      <Suspense fallback={<div className="py-16"><Spinner label="加载中" /></div>}>
        <Routes>
          <Route path="/" element={<OwnerOnly><Dashboard /></OwnerOnly>} />
          <Route path="/stock/:symbol" element={<StockDetail />} />
          <Route path="/screen" element={<Screen />} />
          <Route path="/strategies" element={<Strategies />} />
          <Route path="/strategies/new" element={<StrategyEditor />} />
          <Route path="/strategies/:id/edit" element={<StrategyEditor />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/reports/:runId" element={<BacktestReport />} />
          <Route path="/data" element={<OwnerOnly><DataRedirect /></OwnerOnly>} />
          <Route path="/assistant" element={<Assistant />} />
          <Route path="/signals" element={<Signals />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

/** Where a member lands instead of an owner-only page. */
const MEMBER_HOME = "/screen";

/**
 * Owner-only pages. A member who types the URL, follows an old bookmark or lands on `/`
 * after login is sent to the screener rather than shown a page whose every request
 * returns 403. The server enforces the same rule; this only avoids rendering it.
 *
 * Waits while the role is unknown, so an owner refreshing the dashboard is not bounced
 * away before their profile loads.
 */
function OwnerOnly({ children }: { children: ReactNode }) {
  const isOwner = useIsOwner();
  if (isOwner === undefined) return <div className="py-16"><Spinner label="加载中" /></div>;
  if (!isOwner) return <Navigate to={MEMBER_HOME} replace />;
  return <>{children}</>;
}

/**
 * The data console moved under Settings → 数据. Old links and bookmarks, including the
 * `?job=` progress links, still land on it.
 */
function DataRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set("tab", "data");
  return <Navigate to={`/settings?${params.toString()}`} replace />;
}

function FullPageSpinner({ label }: { label?: string }) {
  return (
    <div className="grid min-h-screen place-items-center">
      <Spinner label={label} />
    </div>
  );
}

function SessionExpired() {
  const logout = useAuthStore((state) => state.logout);
  useEffect(() => {
    logout();
  }, [logout]);
  return <FullPageSpinner label="会话已过期，正在返回登录" />;
}

function NotFound() {
  const location = useLocation();
  return (
    <div className="card p-6 text-center">
      <h1 className="text-lg font-semibold">页面不存在</h1>
      <p className="mt-2 text-sm text-ink-muted">{location.pathname}</p>
    </div>
  );
}
